/**
 * VariableExplorerModal
 * ---------------------------------------------------------------------------
 * Modal premium para explorar e inserir variáveis nos campos de texto dos
 * nodes de fluxo. Layout:
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Inserir Variável                                       [✕] │
 *   ├──────────────┬─────────────────────────────────────────────┤
 *   │ 🔍 Buscar    │  Header da categoria + [+ Nova]            │
 *   │ Sidebar de   │  Tabela: Variável | Descrição | Exemplo    │
 *   │ categorias   │                                            │
 *   └──────────────┴─────────────────────────────────────────────┘
 *
 * - Modal: max-w-5xl, 85dvh (fullscreen no mobile)
 * - Sidebar com 8 categorias + contador por categoria
 * - Mobile: sidebar vira um Select no topo
 * - Busca global filtra em todas as categorias
 * - Categoria "Personalizadas" mostra botão de criar/editar
 *
 * Versão: 1.1.0
 * RELEASE: 2026-09-08 v1.1.0 - Submenus na categoria "Processo (Workflow)"
 *          (Identificadores / Negócio / Cobrança), com filtro e contadores.
 */

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Plus, Copy, Pencil, Trash2, Check, X, Braces } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import {
  CATEGORY_META,
  VariableCategory,
  CatalogVariable,
  VARIABLE_CATALOG,
  CATALOG_TOKEN_SET,
  WORKFLOW_SUBGROUPS,
} from '@/lib/flow/variableCatalog';
import { useTenantVariables, TenantVariable } from '@/hooks/useTenantVariables';

const log = createLogger('VariableExplorerModal');

// ============================================================================
// Tipos
// ============================================================================

/** Item unificado exibido na tabela do modal (catálogo + custom do tenant). */
interface DisplayVariable extends CatalogVariable {
  /** Quando vem de tenant_variables, mantém o registro para permitir edição. */
  tenantRecord?: TenantVariable;
}

export interface VariableExplorerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  /** Callback chamado ao inserir uma variável (recebe o token, ex.: "deal_title"). */
  onInsert: (token: string) => void;
  /** Filtra fora variáveis system (mostra somente customizadas). */
  excludeSystem?: boolean;
  /**
   * Modo de operação:
   * - 'insert' (padrão): inserção de token {{...}} em campos de texto (leitura);
   * - 'destination': escolha de variável GRAVÁVEL (destino de escrita, ex.: bloco
   *   Definir Variável). Lista apenas as personalizadas do cliente e devolve o
   *   registro real via onSelect.
   */
  mode?: 'insert' | 'destination';
  /** Callback do modo 'destination': recebe o TenantVariable real (id + nome). */
  onSelect?: (variable: TenantVariable) => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Converte um TenantVariable que NÃO está no catálogo (= variável customizada
 * ou system legado não migrado) em DisplayVariable, para aparecer no modal.
 */
function tenantVarToDisplay(v: TenantVariable): DisplayVariable {
  return {
    token: v.name,
    label: v.display_name || v.name,
    description: v.description || '',
    example: v.default_value || '—',
    usageExample: `{{${v.name}}}`,
    category: v.is_system ? mapLegacyCategory(v.category) : 'custom',
    type: (v.type as CatalogVariable['type']) || 'text',
    source: v.is_system ? 'system' : 'custom',
    tenantRecord: v,
  };
}

/** Mapeia categorias legadas (do banco) para o novo conjunto de 8 categorias. */
function mapLegacyCategory(legacy: string | null): VariableCategory {
  switch (legacy) {
    case 'contact':
    case 'conversation':
    case 'inbox':
    case 'agent':
    case 'department':
    case 'kanban':
      return 'conversation';
    case 'company':
    case 'datetime':
      return 'general';
    case 'crm':
      return 'deal';
    case 'contract':
      return 'contracts';
    case 'scheduling':
      return 'conversation';
    case 'charge':
      return 'financial';
    default:
      return 'custom';
  }
}

// ============================================================================
// Componente principal
// ============================================================================

export function VariableExplorerModal({
  open,
  onOpenChange,
  tenantId,
  onInsert,
  excludeSystem = false,
  mode = 'insert',
  onSelect,
}: VariableExplorerModalProps) {
  // Modo destino: apenas variáveis graváveis (personalizadas do cliente)
  const isDestination = mode === 'destination';
  const effectiveExcludeSystem = isDestination ? true : excludeSystem;
  const [activeCategory, setActiveCategory] = useState<VariableCategory>(
    isDestination ? 'custom' : 'general'
  );
  // Subgrupo ativo dentro da categoria (hoje só "Processo (Workflow)"); null = todos
  const [activeSubgroup, setActiveSubgroup] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state (criar/editar custom)
  const [fName, setFName] = useState('');
  const [fDisplayName, setFDisplayName] = useState('');
  const [fType, setFType] = useState<TenantVariable['type']>('text');
  const [fDescription, setFDescription] = useState('');

  const {
    variables: tenantVars,
    createVariableAsync,
    updateVariable,
    deleteVariable,
    isCreating,
  } = useTenantVariables(tenantId);

  // -------------------------------------------------------------------------
  // Lista unificada de variáveis (catálogo + custom + system legado não-coberto)
  // -------------------------------------------------------------------------
  const allVariables: DisplayVariable[] = useMemo(() => {
    const catalogItems: DisplayVariable[] = effectiveExcludeSystem
      ? []
      : VARIABLE_CATALOG.map((v) => ({ ...v }));

    // Custom variables (não-sistema) sempre entram
    const customItems = tenantVars
      .filter((v) => !v.is_system)
      .map(tenantVarToDisplay);

    // System legacy: variáveis sistema do banco que NÃO estão no catálogo
    // (mantém retrocompatibilidade caso algo não tenha sido catalogado)
    const legacySystemItems = effectiveExcludeSystem
      ? []
      : tenantVars
          .filter((v) => v.is_system && !CATALOG_TOKEN_SET.has(v.name))
          .map(tenantVarToDisplay);

    return [...catalogItems, ...legacySystemItems, ...customItems];
  }, [tenantVars, effectiveExcludeSystem]);

  // -------------------------------------------------------------------------
  // Agrupamento por categoria + contadores
  // -------------------------------------------------------------------------
  const grouped = useMemo(() => {
    const map = new Map<VariableCategory, DisplayVariable[]>();
    for (const meta of CATEGORY_META) map.set(meta.key, []);
    for (const v of allVariables) {
      const arr = map.get(v.category) || [];
      arr.push(v);
      map.set(v.category, arr);
    }
    return map;
  }, [allVariables]);

  // -------------------------------------------------------------------------
  // Variáveis exibidas (filtradas por busca + categoria ativa)
  // -------------------------------------------------------------------------
  const visibleVariables = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) {
      // Busca global ignora categoria ativa e subgrupo
      return allVariables.filter(
        (v) =>
          v.token.toLowerCase().includes(q) ||
          v.label.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q)
      );
    }
    const daCategoria = grouped.get(activeCategory) || [];
    // v1.1.0: filtro por subgrupo (usado pela categoria Processo/Workflow)
    if (activeSubgroup) return daCategoria.filter((v) => v.subgroup === activeSubgroup);
    return daCategoria;
  }, [search, activeCategory, activeSubgroup, allVariables, grouped]);

  /** Contagem de variáveis por subgrupo da categoria "Processo (Workflow)". */
  const workflowSubgroupCounts = useMemo(() => {
    const itens = grouped.get('workflow') || [];
    return WORKFLOW_SUBGROUPS.map((nome) => ({
      nome,
      count: itens.filter((v) => v.subgroup === nome).length,
    })).filter((s) => s.count > 0);
  }, [grouped]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleInsert = (v: DisplayVariable) => {
    // Modo destino: devolve o registro real da variável gravável
    if (isDestination) {
      if (v.tenantRecord && onSelect) {
        onSelect(v.tenantRecord);
        onOpenChange(false);
        log.debug('Variável de destino selecionada:', v.tenantRecord.name);
      }
      return;
    }
    onInsert(v.token);
    onOpenChange(false);
    log.debug('Variável inserida:', v.token);
  };

  const handleCopy = (token: string) => {
    const text = `{{${token}}}`;
    navigator.clipboard?.writeText(text);
    toast.success(`${text} copiado!`);
  };

  const resetForm = () => {
    setFName('');
    setFDisplayName('');
    setFType('text');
    setFDescription('');
    setEditingId(null);
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!fName.trim()) {
      toast.error('Nome da variável é obrigatório');
      return;
    }
    try {
      const created = await createVariableAsync({
        name: fName.trim(),
        display_name: fDisplayName.trim() || fName.trim(),
        type: fType,
        description: fDescription.trim(),
      });
      resetForm();
      handleInsert(tenantVarToDisplay(created));
    } catch (e) {
      log.error('Erro ao criar variável:', e);
    }
  };

  const handleStartEdit = (v: DisplayVariable) => {
    if (!v.tenantRecord) return;
    setEditingId(v.tenantRecord.id);
    setFName(v.tenantRecord.name);
    setFDisplayName(v.tenantRecord.display_name || '');
    setFType(v.tenantRecord.type);
    setFDescription(v.tenantRecord.description || '');
    setShowForm(true);
    setActiveCategory('custom');
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    updateVariable({
      id: editingId,
      input: {
        display_name: fDisplayName.trim() || fName.trim(),
        type: fType,
        description: fDescription.trim(),
      },
    });
    resetForm();
  };

  const handleDelete = (v: DisplayVariable) => {
    if (!v.tenantRecord) return;
    if (confirm(`Deletar variável "${v.tenantRecord.name}"?`)) {
      deleteVariable(v.tenantRecord.id);
    }
  };

  const activeMeta = CATEGORY_META.find((c) => c.key === activeCategory)!;
  const isSearching = search.trim().length > 0;
  const showCreateButton = activeCategory === 'custom' && !isSearching && !showForm;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { resetForm(); setSearch(''); } }}>
      <DialogContent className="max-w-7xl w-[95vw] h-[85dvh] p-0 gap-0 flex flex-col overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <DialogTitle className="text-lg flex items-center gap-2">
            <Braces className="h-5 w-5 text-primary" />
            {isDestination ? 'Selecionar Variável de Destino' : 'Inserir Variável'}
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Sidebar (desktop) */}
          <aside className="hidden md:flex md:w-56 lg:w-64 shrink-0 border-r border-border bg-muted/30 flex-col">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar variável..."
                  className="h-9 pl-8 text-sm"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <nav className="p-2 space-y-0.5">
                {CATEGORY_META.map((meta) => {
                  const count = grouped.get(meta.key)?.length || 0;
                  if (effectiveExcludeSystem && meta.key !== 'custom') return null;
                  const isActive = !isSearching && activeCategory === meta.key;
                  return (
                    <div key={meta.key}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCategory(meta.key);
                          setActiveSubgroup(null);
                          setSearch('');
                        }}
                        className={cn(
                          'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors',
                          'hover:bg-accent hover:text-accent-foreground',
                          isActive && 'bg-accent text-accent-foreground font-medium'
                        )}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="text-base leading-none">{meta.emoji}</span>
                          <span className="truncate">{meta.label}</span>
                        </span>
                        <Badge
                          variant={isActive ? 'default' : 'secondary'}
                          className="h-5 px-1.5 text-[10px] shrink-0"
                        >
                          {count}
                        </Badge>
                      </button>

                      {/* Submenus da categoria "Processo (Workflow)" */}
                      {isActive && meta.key === 'workflow' && workflowSubgroupCounts.length > 0 && (
                        <div className="mt-0.5 ml-4 space-y-0.5 border-l border-border pl-2">
                          {workflowSubgroupCounts.map((sub) => {
                            const subAtivo = activeSubgroup === sub.nome;
                            return (
                              <button
                                key={sub.nome}
                                type="button"
                                onClick={() => setActiveSubgroup(subAtivo ? null : sub.nome)}
                                className={cn(
                                  'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs transition-colors',
                                  'hover:bg-accent hover:text-accent-foreground',
                                  subAtivo
                                    ? 'bg-accent text-accent-foreground font-medium'
                                    : 'text-muted-foreground'
                                )}
                              >
                                <span className="truncate">{sub.nome}</span>
                                <span className="tabular-nums text-[10px] shrink-0">{sub.count}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </ScrollArea>
          </aside>

          {/* Mobile: busca + select de categoria */}
          <div className="md:hidden border-b border-border p-3 space-y-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar variável..."
                className="h-9 pl-8 text-sm"
              />
            </div>
            {!isSearching && (
              <Select
                value={activeCategory}
                onValueChange={(v) => {
                  setActiveCategory(v as VariableCategory);
                  setActiveSubgroup(null);
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_META.map((meta) => {
                    if (effectiveExcludeSystem && meta.key !== 'custom') return null;
                    const count = grouped.get(meta.key)?.length || 0;
                    return (
                      <SelectItem key={meta.key} value={meta.key}>
                        {meta.emoji} {meta.label} ({count})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            {/* Mobile: subgrupos da categoria "Processo (Workflow)" */}
            {!isSearching && activeCategory === 'workflow' && workflowSubgroupCounts.length > 0 && (
              <Select
                value={activeSubgroup ?? '__todos__'}
                onValueChange={(v) => setActiveSubgroup(v === '__todos__' ? null : v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todos__">Todos os subgrupos</SelectItem>
                  {workflowSubgroupCounts.map((sub) => (
                    <SelectItem key={sub.nome} value={sub.nome}>
                      {sub.nome} ({sub.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Conteúdo principal */}
          <main className="flex-1 min-w-0 flex flex-col">
            {/* Header da categoria */}
            <div className="px-4 sm:px-6 py-3 border-b border-border flex items-center justify-between gap-3 shrink-0">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <span className="text-base leading-none">
                    {isSearching ? '🔍' : activeMeta.emoji}
                  </span>
                  <span className="truncate">
                    {isSearching
                      ? `Resultados para "${search}" (${visibleVariables.length})`
                      : activeSubgroup
                        ? `${activeMeta.label} › ${activeSubgroup}`
                        : activeMeta.label}
                  </span>
                </h3>
                {!isSearching && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {activeMeta.description}
                  </p>
                )}
              </div>
              {showCreateButton && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowForm(true)}
                  className="shrink-0"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Nova Variável
                </Button>
              )}
            </div>

            {/* Form de criar/editar custom */}
            {showForm && (
              <div className="px-4 sm:px-6 py-4 border-b border-border bg-muted/20 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nome (token)</Label>
                    <Input
                      value={fName}
                      onChange={(e) => setFName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      placeholder="ex: nome_cliente"
                      disabled={!!editingId}
                      className="h-8 font-mono text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nome de Exibição</Label>
                    <Input
                      value={fDisplayName}
                      onChange={(e) => setFDisplayName(e.target.value)}
                      placeholder="Nome do Cliente"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo</Label>
                    <Select value={fType} onValueChange={(v) => setFType(v as TenantVariable['type'])}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Texto</SelectItem>
                        <SelectItem value="number">Número</SelectItem>
                        <SelectItem value="boolean">Booleano</SelectItem>
                        <SelectItem value="date">Data</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Telefone</SelectItem>
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="cnpj">CNPJ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 sm:col-span-1">
                    <Label className="text-xs">Descrição</Label>
                    <Input
                      value={fDescription}
                      onChange={(e) => setFDescription(e.target.value)}
                      placeholder="O que essa variável retorna?"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" size="sm" variant="outline" onClick={resetForm}>
                    <X className="h-3.5 w-3.5 mr-1" /> Cancelar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={editingId ? handleSaveEdit : handleCreate}
                    disabled={isCreating}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    {editingId ? 'Salvar' : isDestination ? 'Criar e selecionar' : 'Criar e inserir'}
                  </Button>
                </div>
              </div>
            )}

            {/* Tabela de variáveis */}
            <ScrollArea className="flex-1">
              {visibleVariables.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                  <Braces className="h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">
                    {isSearching
                      ? 'Nenhuma variável encontrada para esse termo.'
                      : activeCategory === 'custom'
                        ? 'Nenhuma variável personalizada criada ainda.'
                        : 'Nenhuma variável nesta categoria.'}
                  </p>
                  {activeCategory === 'custom' && !showForm && !isSearching && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-3"
                      onClick={() => setShowForm(true)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Criar primeira variável
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="sticky top-0 bg-background z-10 border-b border-border">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-4 sm:px-6 py-2 font-medium w-[22%]">Variável</th>
                      <th className="px-3 py-2 font-medium">Descrição</th>
                      <th className="px-3 py-2 font-medium w-[28%]">Exemplo</th>
                      <th className="px-3 py-2 font-medium w-[60px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleVariables.map((v) => (
                      <tr
                        key={`${v.category}-${v.token}-${v.tenantRecord?.id || 'cat'}`}
                        onClick={() => handleInsert(v)}
                        className="border-b border-border/50 hover:bg-accent/40 cursor-pointer transition-colors group"
                      >
                        {/* Coluna 1: nome + token */}
                        <td className="px-4 sm:px-6 py-3 align-top">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-foreground leading-tight">
                              {v.label}
                            </span>
                            <code className="text-[11px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded inline-block w-fit max-w-full truncate">
                              {`{{${v.token}}}`}
                            </code>
                            {isSearching && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 w-fit mt-0.5">
                                {CATEGORY_META.find((m) => m.key === v.category)?.label}
                              </Badge>
                            )}
                          </div>
                        </td>

                        {/* Coluna 2: descrição */}
                        <td className="px-3 py-3 align-top">
                          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 break-words">
                            {v.description || '—'}
                          </p>
                          {v.source === 'virtual' && (
                            <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 mt-1.5">
                              Preenchida em runtime
                            </Badge>
                          )}
                        </td>

                        {/* Coluna 3: exemplo retorno + uso */}
                        <td className="px-3 py-3 align-top">
                          <div className="space-y-1.5">
                            <div>
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                                Retorna
                              </span>
                              <div className="text-xs font-mono bg-muted/60 px-2 py-1 rounded mt-0.5 break-all line-clamp-2">
                                {v.example}
                              </div>
                            </div>
                            <div>
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                                Uso
                              </span>
                              <div className="text-xs bg-muted/30 px-2 py-1 rounded mt-0.5 break-words line-clamp-2 italic text-muted-foreground">
                                {v.usageExample}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Coluna 4: ações */}
                        <td className="px-3 py-3 align-top">
                          <div
                            className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => handleCopy(v.token)}
                              title="Copiar token"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            {v.source === 'custom' && v.tenantRecord && (
                              <>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => handleStartEdit(v)}
                                  title="Editar"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => handleDelete(v)}
                                  title="Excluir"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </ScrollArea>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
